import { useCallback, useEffect, useRef, useState } from 'react'

import type { VaultCategory as VaultCategoryType } from '@api'
import { useUnit } from 'effector-react'
import { Reorder } from 'framer-motion'

import { LoadingBar } from '@/components/ui/LoadingBar.tsx'
import { LocalStorageKeys } from '@/consts/localStorage.ts'
import {
  $allCategoryDetails,
  $canLoadMoreVaultCategoryPages,
  $showMediaItems,
  $vaultCategories,
  $vaultCategoriesError,
  getNextVaultCategoriesPageFx,
  setSelectedCategory,
} from '@/models/vault/categories.ts'
import {
  $isCustomCategory,
  setVaultCategoryCustomOrderFx,
} from '@/models/vault/categoryOptions.ts'
import { openMediaCategory } from '@/models/vault/vault.ts'
import { classNames } from '@/utils/helpers.ts'

import { CustomizeCategoryTitle } from './customize-category-title'
import { VaultCategoriesSkeleton } from './vault-categories-skeleton'
import { VaultCategoriesSort } from './vault-categories-sort'
import { VaultCategoriesTitle } from './vault-categories-title'
import { VaultCategory } from './vault-category'

interface Props {
  showTitle?: boolean
  searchInSort?: boolean
  isInSelect?: boolean
  contentClassName?: string
}

export const VaultCategories = ({
  showTitle,
  searchInSort = false,
  isInSelect = false,
  contentClassName,
}: Props) => {
  const showMediaItems = useUnit($showMediaItems)
  const vaultCategories = useUnit($vaultCategories)
  const allCategoryDetails = useUnit($allCategoryDetails)!
  const isCategoriesLoading = useUnit(getNextVaultCategoriesPageFx.pending)
  const canLoadMore = useUnit($canLoadMoreVaultCategoryPages)
  const isSetCustomCategoryOrderLoading = useUnit(
    setVaultCategoryCustomOrderFx.pending,
  )
  const isCustomCategory = useUnit($isCustomCategory)
  const error = useUnit($vaultCategoriesError)

  const [orderingCategories, setOrderingCategories] = useState<
    VaultCategoryType[]
  >([])

  const loadingRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setOrderingCategories((old) => [
      ...old,
      ...vaultCategories.filter((i) => !old.some((o) => o.id === i.id)),
    ])
  }, [vaultCategories])

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries

      if (
        entry.isIntersecting &&
        vaultCategories.length &&
        !isCategoriesLoading &&
        canLoadMore &&
        !error
      ) {
        getNextVaultCategoriesPageFx()
      }
    },
    [canLoadMore, error, isCategoriesLoading, vaultCategories.length],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: containerRef.current,
      rootMargin: '200px',
      threshold: 0.1,
    })

    if (loadingRef.current) {
      observer.observe(loadingRef.current)
    }

    return () => observer.disconnect()
  }, [handleIntersection])

  const handleSaveOrder = useCallback(async () => {
    await setVaultCategoryCustomOrderFx(orderingCategories.map(({ id }) => id))
  }, [orderingCategories])
  const handleClickAverageCategory = useCallback(
    (category: VaultCategoryType) => {
      if (!isCustomCategory) {
        setSelectedCategory(category)

        if (!isInSelect) {
          sessionStorage.setItem(
            LocalStorageKeys.SELECTED_VAULT_CATEGORY,
            category.id.toString(),
          )
        }

        openMediaCategory()
      }
    },
    [isCustomCategory, isInSelect],
  )

  const handleClickAllCategory = useCallback(() => {
    setSelectedCategory(null)
    openMediaCategory()
    sessionStorage.setItem(LocalStorageKeys.SELECTED_VAULT_CATEGORY, '')
  }, [])

  const getCategoriesContent = () => {
    if (!isCategoriesLoading) {
      if (isCustomCategory) {
        if (!orderingCategories.length) {
          return (
            <div className="text-text-default mt-4 text-center">
              No folder found
            </div>
          )
        }

        return (
          <Reorder.Group
            onReorder={setOrderingCategories}
            values={orderingCategories}
          >
            {orderingCategories.map((c) => (
              <Reorder.Item
                key={c.id}
                value={c}
              >
                <VaultCategory
                  key={c.id}
                  category={c}
                  onClick={handleClickAverageCategory}
                />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )
      }

      if (!vaultCategories.length) {
        return (
          <div className="text-text-default mt-4 text-center">
            No folder found
          </div>
        )
      }
    }

    return vaultCategories.map((c) => (
      <VaultCategory
        key={c.id}
        category={c}
        onClick={handleClickAverageCategory}
      />
    ))
  }

  return (
    <div
      className={classNames(
        'relative flex h-full w-full shrink-0 flex-col border-r-0 pr-0 lg:w-[360px] lg:border-r',
        {
          'lg:flex': showMediaItems,
        },
      )}
    >
      {showTitle && <VaultCategoriesTitle />}
      {!isCustomCategory && <VaultCategoriesSort showSearch={searchInSort} />}
      <div
        ref={containerRef}
        className={classNames(
          'h-[calc(100vh-260px)] overflow-y-scroll pr-4 lg:h-[calc(100vh-264px)]',
          contentClassName,
        )}
      >
        {isCustomCategory ? (
          <CustomizeCategoryTitle
            isSorting={isSetCustomCategoryOrderLoading}
            onSaveOrder={handleSaveOrder}
          />
        ) : (
          <VaultCategory
            category={allCategoryDetails}
            onClick={handleClickAllCategory}
          />
        )}

        {getCategoriesContent()}

        <div
          ref={loadingRef}
          className="h-4 w-full"
        />

        {isCategoriesLoading && !vaultCategories.length && (
          <VaultCategoriesSkeleton />
        )}
      </div>
      {isCategoriesLoading && <LoadingBar rootClassName="absolute bottom-0" />}
    </div>
  )
}
